import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { SidebarInboxButton } from './SidebarInboxButton';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { useCurrentIndexPath } from '@/Config/app/router/useIndexPath.hook';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/Domain/components/Shared/Sidebar/Sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ChartLineIcon, ChartLineIconHandle } from '@/Domain/components/icons/ChartLineIcon';
import {
  FlaskConicalIcon,
  FlaskConicalIconHandle,
} from '@/Domain/components/icons/FlaskConicalIcon';
import { formatUnreadCount } from '@/UI/Insights/UnifiedAiInsights/utils/unreadCount';

const SidebarInsightsButton = ({ isLocked }: { isLocked: boolean }) => {
  const { t } = useTranslation('sidebar_menu');
  const currentPath = useCurrentIndexPath();
  const iconRef = useRef<ChartLineIconHandle>(null);

  const { data: unreadData } = useQuery<{ unread: number; capped: boolean }>({
    queryKey: [QueryKey.analytics.unreadInsightsCount],
    enabled: !isLocked,
  });
  const unread = unreadData?.unread ?? 0;
  const capped = unreadData?.capped ?? false;

  if (isLocked) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={t('menu.conversations.insights')}
          isActive={currentPath == IndexPath.insights}
          className="opacity-50 pointer-events-none"
        >
          <div>
            <ChartLineIcon size={16} />
            <span>{t('menu.conversations.insights')}</span>
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={t('menu.conversations.insights')}
        isActive={currentPath == IndexPath.insights}
        onMouseEnter={() => iconRef.current?.startAnimation()}
        onMouseLeave={() => iconRef.current?.stopAnimation()}
      >
        <NavLink to={IndexPath.insights} data-shortcut="3" viewTransition>
          <ChartLineIcon ref={iconRef} size={16} />
          <span>{t('menu.conversations.insights')}</span>
          {unread > 0 && (
            <Badge
              variant="secondary"
              size="sm"
              className="ml-auto"
              data-testid="sidebar-insights-unread-badge"
            >
              {formatUnreadCount(unread, capped)}
            </Badge>
          )}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const SidebarSimulationButton = ({ isLocked }: { isLocked: boolean }) => {
  const { t } = useTranslation('sidebar_menu');
  const currentPath = useCurrentIndexPath();
  const iconRef = useRef<FlaskConicalIconHandle>(null);

  if (isLocked) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={t('menu.conversations.simulation')}
          isActive={currentPath == IndexPath.simulation}
          className="opacity-50 pointer-events-none"
        >
          <div data-testid="simulation">
            <FlaskConicalIcon size={16} />
            <span>{t('menu.conversations.simulation')}</span>
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={t('menu.conversations.simulation')}
        isActive={currentPath == IndexPath.simulation}
        onMouseEnter={() => iconRef.current?.startAnimation()}
        onMouseLeave={() => iconRef.current?.stopAnimation()}
      >
        <NavLink to={IndexPath.simulation} data-testid="simulation" viewTransition>
          <FlaskConicalIcon ref={iconRef} size={16} />
          <span>{t('menu.conversations.simulation')}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export const SidebarConversationsGroup = ({
  isLocked,
  simulationLocked,
}: {
  isLocked: boolean;
  simulationLocked: boolean;
}) => {
  const { t } = useTranslation('sidebar_menu');

  const content = (
    <SidebarGroup>
      <SidebarGroupLabel>
        {t('menu.conversations.title')}
        {isLocked && <Lock className="ml-auto !size-3" />}
      </SidebarGroupLabel>
      <SidebarMenu>
        <SidebarInboxButton isLocked={isLocked} />
        <SidebarInsightsButton isLocked={isLocked} />
        <SidebarSimulationButton isLocked={isLocked || simulationLocked} />
      </SidebarMenu>
    </SidebarGroup>
  );

  if (isLocked) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{content}</div>
          </TooltipTrigger>
          <TooltipContent side="right">{t('tooltips.locked_section')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};
